import { Button } from "@blueprintjs/core";
import { StoreType } from "polotno/model/store";
import { genTextElement } from "../../../shared/utils/text";
import { TranscriptApi } from "../../../shared/services/transcript.api";

import { useState } from "react";
import { TAny } from "../../../shared/types/common";
import { PageType } from "polotno/model/page-model";
import { ElementType } from "polotno/model/group-model";
import { TTranscriptDTO } from "../../../shared/types/transcript";

const GenTranscript = ({ store }: { store: StoreType }) => {
  const storeJson = store.toJSON() as TAny;
  const videoEl = storeJson.pages[0].children?.find(
    (_: TAny) => _.type === "video"
  );

  const [isGenerating, setIsGenerating] = useState(false);
  const language = "en";

  const blobUrlToFile = async (
    blobUrl: string,
    filename: string,
    mimeType = "video/mp4"
  ) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new File([blob], filename, { type: mimeType });
  };

  const handleGenerateSubtitles = async () => {
    if (!videoEl) return;

    removeOldTranscript();
    setIsGenerating(true);

    try {
      const fileName = videoEl?.src?.split("/").pop();
      const videoFile = await blobUrlToFile(videoEl?.src, fileName);

      if (videoFile) {
        const response = await TranscriptApi.genTranscript(videoFile);
        if (response.success && response.segments.length > 0) {
          addTranscriptElement(response);
        }
      }
    } catch (error) {
      console.error("Error processing video:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const removeOldTranscript = () => {
    const oldIds: string[] = [];
    store.pages.forEach((page: PageType) => {
      page.children.forEach((element: ElementType) => {
        if (element.custom?.type === "transcript") {
          oldIds.push(element.id);
        }
      });
    });

    store.deleteElements(oldIds);
  };

  const addTranscriptElement = (response: TTranscriptDTO) => {
    const canvasWidth = store.width;
    const canvasHeight = store.height;
    const fontSize = 30;
    const lineHeight = 1.2;
    const textWidth = canvasWidth;
    const textX = 0;
    const textYOffset = 10;
    for (const transcript of response.segments) {
      // Calculate how many characters can fit in one line
      const charsPerLine = Math.floor(canvasWidth / (fontSize * 0.6)); // Approximate character width
      const lines = Math.ceil(transcript.text.length / charsPerLine);
      const textHeight = fontSize * lineHeight * lines;
      const textY = canvasHeight - textHeight - textYOffset;
      const textEl = genTextElement(
        transcript.id,
        transcript.text,
        transcript.start,
        transcript.end,
        textWidth,
        textHeight,
        textX,
        textY
      );
      store.activePage.addElement({
        ...textEl,
        custom: { ...textEl?.custom, lang: language, isOriginal: true },
      });
    }

    store.set({
      custom: {
        langSegment: response.segments,
        processId: response.processId,
        isSummarize: false,
      },
    });

    store.openSidePanel("transcript-panel");
  };

  return (
    <Button
      icon="add"
      text={`Generate`}
      intent="primary"
      loading={isGenerating}
      onClick={() => handleGenerateSubtitles()}
    />
  );
};

export default GenTranscript;
